import { useEffect, useRef, useState } from "react";
import { Main, Container } from "../styled";
import close_icon from "../../../../../assets/icons/close.svg";
import Grupo from "../../../../../models/Grupo";
import { convertToBase64 } from "../../../../../utils/conversoes";
import { mensagemDeErro, respostaOk } from "../../../../../utils/resposta";
import { Corpo } from "./styled";

const TIPOS = ["image/png", "image/jpeg", "image/webp"];
const LIMITE_MB = 8;

/**
 * Trocar ou remover a capa do grupo/subgrupo. A acao fica dentro do proprio modal (botao
 * 'Enviando...' e mensagem), sem o overlay 'Aguarde'. Quem decide se a pessoa pode e o
 * servidor (administrador do grupo ou, no subgrupo, do grupo pai).
 */
export default function AlterarCapaModal({ grupo, setGrupo, onClose }) {
  const [arquivo, setArquivo] = useState(null);
  const [previa, setPrevia] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [msg, setMsg] = useState({ tipo: "", texto: "" });
  const botaoFechar = useRef(null);
  const rotulo = grupo.tipo === "Grupo Principal" ? "grupo" : "subgrupo";
  const capaAtual = grupo.foto_capa?.path || null;

  useEffect(() => {
    botaoFechar.current?.focus();
    const esc = (e) => { if (e.key === "Escape" && !enviando) onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose, enviando]);

  useEffect(() => {
    if (!arquivo) { setPrevia(null); return undefined; }
    const url = URL.createObjectURL(arquivo);
    setPrevia(url);
    return () => URL.revokeObjectURL(url);
  }, [arquivo]);

  function escolher(e) {
    const f = e.target.files?.[0];
    setMsg({ tipo: "", texto: "" });
    if (!f) return;
    if (!TIPOS.includes(f.type)) {
      setArquivo(null);
      setMsg({ tipo: "erro", texto: "Use uma imagem PNG, JPG ou WebP." });
      return;
    }
    if (f.size > LIMITE_MB * 1024 * 1024) {
      setArquivo(null);
      setMsg({ tipo: "erro", texto: `A imagem passa de ${LIMITE_MB} MB. Escolha uma menor.` });
      return;
    }
    setArquivo(f);
  }

  async function salvar() {
    if (!arquivo || enviando) return;
    setEnviando(true);
    setMsg({ tipo: "", texto: "" });
    try {
      const [midia] = await convertToBase64([arquivo]);
      const resultado = await Grupo.editarCapa(grupo.id, { base64: midia.base64, tipo: "Imagem" });
      if (respostaOk(resultado)) {
        // mostra a capa nova na hora (pagina do grupo e cartoes leem de grupo.foto_capa)
        setGrupo({ ...grupo, foto_capa: resultado.data?.foto_capa || grupo.foto_capa });
        setArquivo(null);
        setMsg({ tipo: "ok", texto: "Capa atualizada com sucesso!" });
      } else {
        setMsg({ tipo: "erro", texto: mensagemDeErro(resultado, "Não foi possível salvar a capa. Tente novamente.") });
      }
    } catch (err) {
      setMsg({ tipo: "erro", texto: "Não foi possível ler a imagem. Tente outra." });
    }
    setEnviando(false);
  }

  async function remover() {
    if (enviando || !capaAtual) return;
    setEnviando(true);
    setMsg({ tipo: "", texto: "" });
    const resultado = await Grupo.removerCapa(grupo.id);
    if (respostaOk(resultado)) {
      setGrupo({ ...grupo, foto_capa: null });
      setArquivo(null);
      setMsg({ tipo: "ok", texto: "Capa removida. O grupo volta a usar a imagem padrão." });
    } else {
      setMsg({ tipo: "erro", texto: mensagemDeErro(resultado, "Não foi possível remover a capa. Tente novamente.") });
    }
    setEnviando(false);
  }

  return (
    <Main>
      <Container role="dialog" aria-modal="true" aria-labelledby="capa-titulo">
        <div className="content-modal">
          <section>
            <div className="close">
              <button type="button" ref={botaoFechar} onClick={onClose} disabled={enviando} aria-label="Fechar">
                <img src={close_icon} alt="" />
              </button>
            </div>
          </section>

          <section>
            <div className="title"><h2 id="capa-titulo">Capa do {rotulo}</h2></div>
          </section>

          <Corpo>
            <div className="previa">
              {previa || capaAtual
                ? <img src={previa || capaAtual} alt={previa ? "Prévia da nova capa" : `Capa atual do ${rotulo}`} />
                : <span className="sem-capa">Este {rotulo} está usando a imagem padrão.</span>}
            </div>

            <p className="dica">Formatos PNG, JPG ou WebP, até {LIMITE_MB} MB. A imagem aparece em faixa larga, então prefira uma foto horizontal.</p>

            <div className="arquivo">
              <label className="escolher">
                {arquivo ? "Escolher outra imagem" : "Escolher imagem"}
                <input type="file" accept="image/png,image/jpeg,image/webp" onChange={escolher} disabled={enviando} />
              </label>
              {arquivo && <span className="nome-arquivo">{arquivo.name}</span>}
            </div>

            <p className={`mensagem ${msg.tipo}`} role="status" aria-live="polite">{msg.texto}</p>

            <div className="acoes">
              <button type="button" className="salvar" onClick={salvar} disabled={!arquivo || enviando}>
                {enviando ? "Enviando…" : "Salvar capa"}
              </button>
              {capaAtual && (
                <button type="button" className="remover" onClick={remover} disabled={enviando}>Remover capa</button>
              )}
            </div>
          </Corpo>
        </div>
      </Container>
    </Main>
  );
}
